import 'firebase/compat/auth';
import { useEffect, useRef } from 'react';
import { userEmailVerificationPath } from '../../../functions/src/admin/paths';
import { auth, functions, mainDatabase } from '../../api/firebase';
import type { DataSnapshot } from '../../api/firebase';
import type { VerificationData } from '../../types';
import { isBenignAuthNetworkError, isBenignAuthPersistenceError } from '../../api/authPersistence';

type PopulateEmailVerificationArgs = {
    uid: string;
    isGoogleUser: boolean;
    existingVerification?: VerificationData | null;
};

/**
 * Sync Auth emailVerified → main DB when needed.
 * Gmail users skip client reload (server uses Admin SDK); avoids IndexedDB errors on profile load.
 */
export async function populateEmailVerificationIfNeeded({
    uid,
    isGoogleUser,
    existingVerification,
}: PopulateEmailVerificationArgs): Promise<void> {
    const user = auth.currentUser;
    if (!user || user.uid !== uid) {
        return;
    }
    if (existingVerification?.verified === true) {
        return;
    }

    const populateEmailVerification = functions.httpsCallable('populateEmailVerificationForExistingUsers');

    if (isGoogleUser) {
        await populateEmailVerification();
        return;
    }

    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
    }

    let emailVerified = user.emailVerified;
    try {
        await user.reload();
        emailVerified = auth.currentUser?.emailVerified ?? emailVerified;
    } catch (error) {
        if (isBenignAuthPersistenceError(error) || isBenignAuthNetworkError(error)) {
            return;
        }
        throw error;
    }

    const verificationSnapshot = await mainDatabase.ref(userEmailVerificationPath(uid)).once('value');
    const latestVerification: VerificationData | null = verificationSnapshot.val();
    if (latestVerification?.verified === emailVerified) {
        return;
    }

    await populateEmailVerification();
}

/** Run once after the verification RTDB node has loaded. */
export function usePopulateEmailVerification(
    uid: string | undefined,
    verificationSnapshot: DataSnapshot | undefined,
    emailVerification: VerificationData | undefined,
    isGoogleUser: boolean,
): void {
    const attemptedRef = useRef(false);
    useEffect(() => {
        if (!uid || attemptedRef.current || verificationSnapshot === undefined) {
            return;
        }
        if (emailVerification?.verified === true) {
            attemptedRef.current = true;
            return;
        }
        attemptedRef.current = true;
        void populateEmailVerificationIfNeeded({
            uid,
            isGoogleUser,
            existingVerification: emailVerification,
        }).catch((error: unknown) => {
            if (!isBenignAuthPersistenceError(error) && !isBenignAuthNetworkError(error)) {
                console.error('Error populating email verification status:', error);
            }
        });
    }, [uid, verificationSnapshot, emailVerification?.verified, isGoogleUser]);
}
